<template>
  <Transition
    @enter="onEnter"
    @leave="onLeave"
    appear
  >
    <div
      class="modal-story"
      v-if="state.story"
    >
      <HeadMeta :story="state.story" />

      <FocusTrap :active="!!state.story">
        <TheSmoothScroll :useWrapper="true">
          <div
            class="underlay"
            @click="onClose"
          />
          <div class="pad">
            <div class="wrapper">
              <div class="content">
                <div class="preheading type-mono">
                  {{ state.story?.content.type }}
                </div>
                <h2 class="name">{{ state.story?.name }}</h2>
                <div class="subheading">
                  {{ state.story.content.subheading }}
                </div>
                <div
                  class="tags type-mono"
                  v-if="tags.length"
                >
                  <div
                    class="tag"
                    v-for="(tag, idx) in tags"
                    :key="tag"
                  >
                    {{ tag }}
                    <div
                      class="dot"
                      v-if="idx < tags.length - 1"
                    />
                  </div>
                </div>
                <Media
                  :blok="state.story.content.hero"
                  class="image"
                />
                <div class="body">
                  <RenderRichText :document="state.story.content.body" />

                  <!-- <CtaButton v-if="state.story.content.type == 'job'"> Job </CtaButton> -->
                </div>
              </div>

              <div class="top-right">
                <div
                  class="languages type-mono"
                  v-if="state.languageItems.length > 1"
                >
                  <a
                    class="language"
                    v-for="item in state.languageItems"
                    :key="item.uuid"
                    :to="`/${item.full_slug}`"
                    :active="item.active ? true : null"
                    @click.prevent="onLanguageClick($event, item)"
                  >
                    {{ item.langLabel }}
                  </a>
                </div>

                <ButtonIcon
                  class="btn-close"
                  icon="close"
                  theme="story"
                  @click="onClose"
                />
              </div>

              <div
                class="loading"
                :active="loading ? true : null"
              ></div>
            </div>
          </div>
        </TheSmoothScroll>
      </FocusTrap>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import dayjs from "dayjs"
import { FocusTrap } from "focus-trap-vue"

const props = defineProps({})
const emits = defineEmits([])

const env = useEnv()
const { $gsap, $ScrollTrigger } = useNuxtApp()
const { app, debug, storyblok } = useStores()
const storyapi = useStoryblokApi()

const refRoot = ref(null)

// **********************************************************************
// STORY DATA
// **********************************************************************

// const story = ref(null)
const state = reactive({ story: null, languageItems: [] })

// **********************************
// DEEP LINK
// **********************************
const route = useRoute()
const { slug } = route.params
const folder = route.params.slug[0]
const folders = ["projects", "environments", "products", "jobs", "news"]

if (folders.includes(folder)) {
  const path = slug.join("/")
  state.story = await useStory(path)

  app.setModalStoryId(state.story.uuid)
}

// **********************************
// CLICK ON BUTTON, GET STORY BY ID
// **********************************
const updateStoryById = async () => {
  if (state.story && state.story.uuid == app.modalStoryId) return

  if (!app.modalStoryId) {
    state.story = null
    return
  }

  const resp = await storyapi.get(`cdn/stories/${app.modalStoryId}`, {
    find_by: "uuid",
    version: storyblok.version,
  })

  state.story = resp.data.story
}

watch(() => app.modalStoryId, updateStoryById)

const onClose = () => {
  app.setModalStoryId(null)
  state.story = null
}

// **********************************************************************
// CMS LIVE UPDATE
// **********************************************************************

onMounted(() => {
  if (!state.story) return
  useStoryblokBridge(state.story.id, (story) => {
    state.story = story
  })
})

// **********************************************************************
// TAGS
// **********************************************************************
const tags = computed(() => {
  let _tags = []
  if (state.story?.content.tags) {
    _tags = [..._tags, ...state.story.content.tags.split("\n")]
  }

  if (state.story?.content.date) {
    let d = dayjs(state.story.content.date)
    _tags.push(d.format("DD MMM YYYY"))
  }

  if (state.story?.content.meta?.[0]?.location) {
    _tags.push(state.story.content.meta[0].location)
  }

  return _tags
})

// **********************************************************************
// Lock Scroller
// **********************************************************************
const show = computed(() => {
  return !!state.story
})

watch(
  () => show.value,
  () => {
    app.setScrollPaused(show.value)
  }
)

// **********************************************************************
// Update URL on Open/Close
// **********************************************************************
const originalPathname = ref(null)

const updateUrl = () => {
  // CLOSE
  if (!state.story) {
    if (!originalPathname.value || folders.includes(folder)) {
      window.history.replaceState(null, "", "/")
    } else {
      window.history.replaceState(null, "", originalPathname.value)
      originalPathname.value = null
    }

    return
  }

  // OPEN
  originalPathname.value = window.location.pathname

  const base = window.location.origin
  const pathname = window.location.pathname
  const search = window.location.search

  const storySlug = state.story.full_slug

  const url = `${base}/${storySlug}${search}`
  window.history.replaceState(null, "", url)
}

watch(() => state.story, updateUrl)

// **********************************************************************
// LANGUAGES
// **********************************************************************
const updateLangItems = () => {
  if (!state.story || !state.story?.full_slug.startsWith("job")) {
    state.languageItems = []
    return
  }
  // if (!state.story.full_slug.startsWith("job")) {
  //   state.languageItems = []
  //   return
  // }

  let items = []

  // CURRENT ITEM
  let currItem = {
    full_slug: state.story.full_slug,
    id: state.story.id,
    lang: state.story.full_slug.split("/")[1],
  }
  currItem.langLabel = getLangLabel(currItem.lang)
  currItem.active = true
  items.push(currItem)

  // ALTERNATE ITEMS
  if (state.story?.alternates?.length) {
    console.log(JSON.parse(JSON.stringify(state.story.alternates, null, 2)))
    const altSlugs = state.story.alternates.map((alt) => alt.full_slug)
    const altLangs = altSlugs.map((altSlug) => {
      return altSlug.split("/")[1]
    })

    const _items = state.story.alternates.map((alt) => {
      let i = {
        full_slug: alt.full_slug,
        uuid: alt.uuid,
        id: alt.id,
        lang: alt.full_slug.split("/")[1],
        active: false,
      }
      i.langLabel = getLangLabel(i.lang)
      return i
    })

    items = [...items, ..._items]
  }

  // SORT ITEMS
  items.sort((a, b) => {
    if (a.lang == "en") return -1
    if (b.lang == "en") return 1
    return a.langLabel.localeCompare(b.langLabel)
  })

  state.languageItems = items
}
watch(() => state.story, updateLangItems)
onMounted(updateLangItems)

const getLangLabel = (lang) => {
  if (!storyblok.datasources.languages) return lang
  return storyblok.datasources.languages.find((l) => l.value == lang).name
}

const loading = ref(false)
const onLanguageClick = async (evt, item) => {
  evt.preventDefault()

  loading.value = true

  try {
    const resp = await storyapi.get(`cdn/stories/${item.id}`, {
      // find_by: "uuid",
      version: storyblok.version,
    })

    state.story = resp.data.story
  } finally {
    loading.value = false
  }
}

// **********************************************************************
// ANIMATION
// **********************************************************************

const onEnter = function (el, done) {
  const tl = $gsap.timeline({ onComplete: done })
  const q = $gsap.utils.selector(el)
  const underlay = q(".underlay")
  const wrapper = q(".wrapper")

  tl.add("start")
  tl.fromTo(
    underlay,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      duration: 0.4,
    },
    "start"
  )

  tl.fromTo(
    wrapper,
    {
      scale: 0.9,
      opacity: 0,
    },
    {
      scale: 1,
      opacity: 1,
      duration: 0.5,
    },
    "start+=0.1"
  )
}
const onLeave = function (el, done) {
  const tl = $gsap.timeline({ onComplete: done })
  const q = $gsap.utils.selector(el)
  const underlay = q(".underlay")
  const wrapper = q(".wrapper")

  tl.add("start")
  tl.to(
    underlay,
    {
      opacity: 0,
      duration: 0.3,
    },
    "start"
  )

  tl.to(
    wrapper,
    {
      scale: 0.9,
      opacity: 0,
      duration: 0.3,
    },
    "start"
  )
}
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";

.modal-story {
  @include fill;
  position: fixed;
  z-index: 1000;
  @include flex(column, center, center);
  // padding-block: $p6;
}

.underlay {
  @include fill;
  position: fixed;
  background: rgba(black, 0.59);
  backdrop-filter: blur(20px);
}

.pad {
  padding-block: $p6;
}

.wrapper {
  --pad: 4rem;

  position: relative;
  width: 82rem;
  min-height: 10rem;
  max-width: calc(100% - var(--pad) * 2);
  border-radius: 2rem;
  background: var(--cl-light);
  color: var(--cl-black);
  padding: var(--pad);
  padding-bottom: calc(var(--pad) * 2);
  margin: 0 auto;
}

.the-smooth-scroll {
  padding-block: $p6;
}

.content {
  padding-top: 4.8rem;
}

.preheading {
}

.name {
}

.subheading {
  margin-block: $p3 $p4;
}

.tags {
  @include flex(row, flex-start, center);
  flex-wrap: wrap;
  gap: $p2;
  color: rgba($cl-black, 0.6);

  .tag {
    @include flex(row, flex-start, center);
    gap: $p2;
  }

  .dot {
    width: 0.4rem;
    height: 0.4rem;
    background: var(--cl-black);
    border-radius: 50%;
  }
}

:deep(.image) {
  width: 100%;
  margin-block: $p4;
  border-radius: $p1;
}
.body {
  position: relative;
  :deep(p) {
    color: black;
  }
}

.top-right {
  position: absolute;
  top: var(--pad);
  right: var(--pad);
  @include flex(row, flex-end, center);
}

.languages {
  @include flex(row, flex-start, center);
  gap: $p2;
  margin-right: $p3;
}
.language {
  opacity: 0.4;
  &[active] {
    opacity: 1;
    border-bottom: 1px solid var(--cl-black);
  }
}

.loading {
  @include fill;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 2rem;
  transition: all 0.2s ease-out;
  opacity: 0;
  pointer-events: none;

  &[active] {
    opacity: 1;
    backdrop-filter: blur(5px);
    will-change: filter;
    transition: all 1s ease-out;
  }
}

@include mobile {
  .wrapper {
    --pad: 2rem;
  }
}
</style>
